import { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useCamera } from '@pella/barcode-scanner';
import { useAction, useCreateRequest, useDuplicateRequestCheck } from '../../../hooks';
import { safeJSONParse } from '../../../utils';

import { Button, toast } from '@pella/sash-framework';
import { DuplicateRequestModal } from '../../atoms';

import * as classes from './QuickRequest.module.css';

/**
 * Component for Quick Request page
 */
export const QuickRequest = () => {
  const navigate = useNavigate();
  const { startCamera } = useCamera();

  const [barcode, setBarcode] = useState(
    /**@type {Pella.ProductionRequestAPI.CreateRequestInput|undefined} */ (undefined),
  );

  const { data: action, isPending } = useAction(barcode?.actionId || '');

  const { mutate: createRequest, isPending: isCreatingRequest } = useCreateRequest();

  /** Close the quick request page */
  const close = () => navigate('/');

  const {
    duplicateRequests,
    submitWithCheck,
    onCancel: onDuplicateCancel,
    onProceed: onDuplicateProceed,
    isChecking,
  } = useDuplicateRequestCheck(close);

  const showConfirmation = Boolean(barcode && action);
  const isSubmitting = isCreatingRequest || isChecking;

  // Start camera and handle barcode scanning on component mount
  useEffect(() => {
    const onScan = (/** @type {string} */ result) => {
      const parsed = safeJSONParse(result);

      if (!parsed) {
        toast.error('Invalid barcode scanned.');
        navigate('/');
        return;
      }

      setBarcode(parsed);
    };

    const onClose = () => navigate('/');

    startCamera(onScan, onClose);
  }, [startCamera, navigate]);

  if (barcode && !action && !isPending) {
    return <Navigate to='/' replace />;
  }

  /** Handle quick request submit, warning about open duplicate requests before creating */
  const onSubmit = () => {
    /* v8 ignore next */
    if (barcode) submitWithCheck(barcode, (input) => createRequest(input, { onSettled: close }));
  };

  return (
    showConfirmation && (
      <div className={classes.container}>
        <div>
          <h2>Confirm Request</h2>
          <div className={classes.actionTitle}>
            <div>{action?.description}</div>
            <div>{action?.message}</div>
          </div>
          <div className={classes.actionDetails}>
            {barcode?.comment && (
              <div className={classes.actionDetailItem}>
                <span>{barcode.comment}</span>
              </div>
            )}
            {barcode?.dropDownListVal1 && (
              <div className={classes.actionDetailItem}>
                <b>List 1: </b>
                {barcode.dropDownListVal1}
              </div>
            )}
            {barcode?.dropDownListVal2 && (
              <div className={classes.actionDetailItem}>
                <b>List 2: </b>
                {barcode.dropDownListVal2}
              </div>
            )}
            {barcode?.dynamicInput && action?.showDynamicInput && action?.actionPrompt && (
              <div className={classes.actionDetailItem}>
                <b>{action.actionPrompt}: </b>
                {barcode.dynamicInput}
              </div>
            )}
            {barcode?.requestQty && (
              <div className={classes.actionDetailItem}>
                <b>Quantity: </b>
                {barcode.requestQty}
              </div>
            )}
          </div>
        </div>
        <div className={classes.buttons}>
          <Button color='secondary' onClick={close} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button color='primary' onClick={onSubmit} disabled={isSubmitting}>
            Submit
          </Button>
        </div>
        <DuplicateRequestModal
          isOpen={duplicateRequests.length > 0}
          requests={duplicateRequests}
          onCancel={onDuplicateCancel}
          onProceed={onDuplicateProceed}
        />
      </div>
    )
  );
};
